import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  TextField,
} from "@shopify/polaris";
import { getMerchantId, getToken, subscribe, clearToken } from "./token.js";
import { DevSessionError, mintDevSession } from "./dev-session.js";
import { mintSessionAuth, SessionAuthError } from "./session-auth.js";
import { isEmbedded } from "../lib/appBridge.js";

interface DevAuthGateProps {
  readonly children: ReactNode;
}

// Blocks the rest of the app behind whichever auth path matches the runtime:
// - Embedded (launched from Shopify Admin, `?host=...` present) → App Bridge
//   session token → POST /auth/session → our JWT. Silent, no form.
// - Local dev (no `host` param) → manual merchantId form → POST /auth/dev/session.
export function DevAuthGate({ children }: DevAuthGateProps): JSX.Element {
  const [token, setTokenState] = useState<string | null>(getToken());

  useEffect(() => {
    return subscribe(() => setTokenState(getToken()));
  }, []);

  if (!token) return isEmbedded() ? <EmbeddedSignIn /> : <DevSignInForm />;
  return <>{children}</>;
}

function EmbeddedSignIn(): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);

  const mint = useCallback(async () => {
    setError(null);
    setReason(null);
    try {
      await mintSessionAuth();
    } catch (err) {
      if (err instanceof SessionAuthError) {
        setError(err.message);
        setReason(err.reason);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to sign in to Shopify.");
      }
    }
  }, []);

  useEffect(() => {
    void mint();
  }, [mint]);

  if (error) {
    return (
      <Page title="Connecting to Shopify">
        <BlockStack gap="400">
          <Banner tone="critical" title="Could not complete sign-in">
            <p>{error}</p>
            {reason === "merchant_not_installed" ? (
              <p>
                This store hasn't completed installation yet. Re-open the app from your
                Shopify Admin to finish setup.
              </p>
            ) : null}
          </Banner>
          <InlineStack align="end">
            <Button onClick={() => void mint()}>Try again</Button>
          </InlineStack>
        </BlockStack>
      </Page>
    );
  }

  return (
    <Page title="Signing in to Shopify…">
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="200">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={3} />
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

function DevSignInForm(): JSX.Element {
  const [merchantId, setMerchantId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = useCallback(async () => {
    if (!merchantId.trim()) {
      setError("Merchant ID is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await mintDevSession(merchantId.trim());
    } catch (err) {
      if (err instanceof DevSessionError) {
        setError(`${err.message} (HTTP ${err.status})`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to sign in.");
      }
    } finally {
      setBusy(false);
    }
  }, [merchantId]);

  return (
    <Page title="Dev sign-in" subtitle="Dev-only path — Shopify App Bridge is used in production.">
      <BlockStack gap="400">
        <Banner tone="info" title="Dev environment">
          <p>
            This form posts to <code>POST /auth/dev/session</code>, which is disabled in
            production. Enter the merchant id you want to sign in as. Open the app from
            Shopify Admin for the embedded flow instead.
          </p>
        </Banner>
        <Card>
          <BlockStack gap="400">
            <TextField
              label="Merchant ID"
              value={merchantId}
              onChange={setMerchantId}
              autoComplete="off"
              autoFocus
              disabled={busy}
              onBlur={() => {
                /* no-op — keeps TextField controlled */
              }}
            />
            {error ? (
              <Banner tone="critical" title="Could not sign in">
                <p>{error}</p>
              </Banner>
            ) : null}
            <InlineStack align="end">
              <Button variant="primary" loading={busy} onClick={onSubmit}>
                Sign in
              </Button>
            </InlineStack>
            <Box>
              <Text as="p" tone="subdued" variant="bodySm">
                The issued token is stored in memory only; a page reload signs you out.
              </Text>
            </Box>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

export function signOut(): void {
  clearToken();
}

export function useCurrentMerchantId(): string | null {
  const [id, setId] = useState(getMerchantId());
  useEffect(() => subscribe(() => setId(getMerchantId())), []);
  return id;
}
